// 003 (FR-005 to FR-011, FR-017 to FR-020; research R5; data-model §5): what /join/<CODE> shows,
// from the code, the lookup and the session. Pure; the screen renders it and runs the redirects.
import type { AppError } from "@/api/errors";
import type { Api } from "@/api/types";
import { servedRoles } from "@/session/store";
import type { Platform, SessionState } from "@/session/types";

import type { LookupState } from "./schoolLookup";

type School = Api["SchoolByCode"];
type LinkSource = "LINK" | "QR";

export type JoinView =
  | { kind: "loading" }
  | { kind: "invalid"; signedIn: boolean }
  | { kind: "notActive"; name: string; signedIn: boolean }
  | { kind: "rateLimited"; signedIn: boolean }
  | { kind: "error"; error: AppError }
  | { kind: "welcome"; school: School; source: LinkSource }
  | { kind: "handOff"; school: School; source: LinkSource }
  | { kind: "switchThenHandOff"; school: School; source: LinkSource }
  | { kind: "parentRoleOffer"; platform: Platform };

export function decideJoinView(input: {
  codeValid: boolean;
  lookup: LookupState;
  session: SessionState;
  platform: Platform;
  source: LinkSource;
}): JoinView {
  const { codeValid, lookup, session, platform, source } = input;
  const signedIn = session.status === "signedIn";
  if (!codeValid) return { kind: "invalid", signedIn };
  if (session.status === "restoring" || lookup.status === "loading")
    return { kind: "loading" };
  switch (lookup.kind) {
    case "invalid":
      return { kind: "invalid", signedIn };
    case "notActive":
      return { kind: "notActive", name: lookup.name, signedIn };
    case "rateLimited":
      return { kind: "rateLimited", signedIn };
    case "error":
      return { kind: "error", error: lookup.error };
    case "active":
      break;
  }
  const school = lookup.school;
  if (session.status !== "signedIn") return { kind: "welcome", school, source };
  const served = servedRoles(session.roles);
  if (!served.includes("PARENT")) return { kind: "parentRoleOffer", platform };
  if (platform === "web" && session.activeRole !== "PARENT")
    return { kind: "switchThenHandOff", school, source };
  return { kind: "handOff", school, source };
}

/** Source on the landing: the `src` parameter, else a same-code pending join's, else LINK (R4). */
export function landingSource(
  src: string | string[] | undefined,
  pending: { code: string; source: LinkSource } | null,
  code: string,
  fromLink: (src: string | string[] | undefined) => LinkSource,
): LinkSource {
  if (src !== undefined) return fromLink(src);
  return pending?.code === code ? pending.source : "LINK";
}
